<template>
    <div id="lt-yantr" class="advanced-calc rent-vs-buy-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <ul class="filter-inputs five-col">
                    <li class="zip-code-input">
                        <label>{{ labelPropertyLocation }}</label>
                        <zip-code-input
                            v-model="ZipCode"
                            :autofocus="true"
                            @input="getLocationByZip"
                        />
                        <span v-show="ShowZipCodeError" class="error-msg">Please enter a valid zip code.</span>
                        <div v-show="!ShowZipCodeError" class="address">
                            <span class="lt4-MapPin"></span> {{ Location.city + ', ' + Location.state }}
                        </div>
                    </li>
                    <li>
                        <number-input
                            v-model="DesiredHomePrice"
                            :label="labelDesiredHomePrice"
                            inputmode="decimal"
                            maxlength="7"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="DesiredRentPayment"
                            :label="labelDesiredRentPayment"
                            inputmode="decimal"
                            maxlength="5"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="PlannedStay"
                            :label="labelPlannedStay"
                            symbol="Yrs"
                            symbol-aligned="right"
                            maxlength="2"
                            inputmode="decimal"
                            @inputBlur="restrictPlannedStay"
                        />
                    </li>
                </ul>
                <!-- Advanced Options start -->
                <div v-show="ShowAdvancedOptions" class="advanced-options" :class="{ 'all-options': ShowAdvancedOptions, 'less-options' : !ShowAdvancedOptions}">
                    <div class="filter-title">Homebuying</div>
                    <ul class="filter-inputs five-col">
                        <li class="percentage-amt">
                            <number-input
                                v-model="DownPayment"
                                :label="labelDownPayment"
                                inputmode="decimal"
                                maxlength="7"
                                :tooltip-link="DownPaymentTooltip"
                                @input="downPaymentChange"
                            />
                        </li>
                        <li class="percentage-input">
                            <number-input
                                v-model="DownPaymentPercentage"
                                symbol="%"
                                symbol-aligned="right"
                                maxValue="100.00"
                                precision="2"
                                inputmode="decimal"
                                @input="downPaymentPercentChange"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="InterestRate"
                                :label="labelInterestRate"
                                symbol="%"
                                symbol-aligned="right"
                                maxValue="100.000"
                                precision="3"
                                inputmode="decimal"
                            />
                        </li>
                        <li>
                            <drop-down
                                v-model="MortgageTerm"
                                :label="labelMortgageTerm"
                                :options="MortgageTermYearsList"
                            />
                        </li>
                        <li class="inputs-subtext percentage-amt">
                            <number-input
                                v-model="PropertyTaxValue"
                                :label="labelPropertyTax"
                                @input="propertyTaxValueChange"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li class="percentage-input">
                            <number-input
                                v-model="PropertyTax"
                                symbol="%"
                                symbol-aligned="right"
                                maxValue="100.00"
                                precision="2"
                                inputmode="decimal"
                                @input="propertyTaxChange"
                            />
                        </li>
                    </ul>
                    <ul class="filter-inputs five-col mob-ai-baseline">
                        <li class="inputs-subtext">
                            <number-input
                                v-model="HomeownersInsurance"
                                :label="labelHomeownersInsurance"
                                inputmode="decimal"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="MonthlyHOAFees"
                                :label="labelAnnualHOAFees"
                                inputmode="decimal"
                                maxlength="6"
                            />
                            <span class="subtext-val">/month</span>
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="AnnualMaintenance"
                                :label="labelAnnualMaintenance"
                                inputmode="decimal"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li>
                            <number-input
                                v-model="HomeAppreciation"
                                :label="labelHomeAppreciation"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                    </ul>
                    <ul class="filter-inputs five-col mob-ai-baseline">
                        <li>
                            <number-input
                                v-model="CostSellingHome"
                                :label="labelCostSellingHome"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="ClosingCosts"
                                :label="labelClosingCosts"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="InvestmentReturnRate"
                                :label="labelInvestmentReturnRate"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="Inflation"
                                :label="labelInflation"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                    </ul>
                    <div class="filter-title">Tax Options</div>
                    <ul class="filter-inputs five-col mob-ai-baseline">
                        <li class="inputs-subtext">
                            <number-input
                                v-model="AnnualIncome"
                                :label="labelAnnualIncome"
                                :tooltip-link="AnnualIncomeTooltip"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li>
                            <number-input
                                v-model="MarginalIncomeTax"
                                :label="labelMarginalIncomeTax"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                        <li>
                            <number-input
                                v-model="AvgSavingAndLoan"
                                :label="labelAvgSavingAndLoan"
                                maxValue="100.00"
                                precision="2"
                                inputmode="decimal"
                                symbol="%"
                                symbol-aligned="right"
                            />
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="OtherTaxDeduction"
                                :label="labelOtherTaxDeduction"
                                inputmode="decimal"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                        <li class="include-pmi">
                            <div class="form-checkbox">
                                <input id="includeMarried" v-model="IsMarried" type="checkbox" />
                                <label for="includeMarried">Married</label>
                            </div>
                        </li>
                    </ul>
                    <div class="filter-title">Renting</div>
                    <ul class="filter-inputs five-col">
                        <li>
                            <number-input
                                v-model="RentalInflation"
                                :label="labelRentalInflation"
                                symbol="%"
                                symbol-aligned="right"
                                precision="2"
                                inputmode="decimal"
                            />
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="SecurityDeposit"
                                :label="labelSecurityDeposit"
                                inputmode="decimal"
                                maxlength="6"
                            />
                            <span class="subtext-val">one-time</span>
                        </li>
                        <li class="inputs-subtext">
                            <number-input
                                v-model="RentalInsurance"
                                :label="labelRentalInsurance"
                                inputmode="decimal"
                            />
                            <span class="subtext-val">/year</span>
                        </li>
                    </ul>
                </div>
                <a v-show="ShowAdvancedOptions" href="javascript:void(0)" class="reset-calc hidden-xs" @click="resetFilter()"><span class="lt-icons lt4-Refresh"></span> Reset Calculator</a>
                <!-- Advanced Options end-->
                <button v-show="ShowAdvancedOptions" class="btn btn-blue recalculate" type="submit" @click="calculateAndViewMore()">{{ recalculateButtonText }}</button>
                <label class="show-hide-options" :class="{ 'hidden-xs': ShowAdvancedOptions}" @click="viewMoreClick()">
                    <span class="lt-icons" :class="{ 'lt4-MinusCircle': ShowAdvancedOptions, 'lt4-PlusCircle' : !ShowAdvancedOptions}"></span> {{ !ShowAdvancedOptions ? "" : "Hide" }} Advanced Options
                </label>
            </div>
            <div class="buying-section">
                <div class="heading">
                    <h4><b>We Recommend</b></h4>
                    <div v-if="!isBuyingExpensive()" class="result">Buying</div>
                    <div v-else class="result">Renting</div>
                    <h4 v-html="GraphResult"></h4>
                </div>
                <div class="text-center">
                    <button class="btn btn-blue" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                    <div class="clear-fix"></div>
                    <advertising-disclosures
                        v-if="DisclosureText"
                        :disclosure-text="DisclosureText"
                        :disclosure-tokens="DisclosureTokens"
                    />
                    <lt-logo v-if="ltLogo == 1">
                        <small>NMLS#1136 Terms & Conditions Apply</small>
                    </lt-logo>
                </div>
                <div class="graph-block">
                    <div class="line-chart">
                        <div>
                            <vue-chart
                                type="line"
                                :data="chartData"
                                :options="options"
                                :width="ChartWidth"
                                :height="ChartHeight"
                            />
                        </div>
                    </div>
                    <ul class="chart-info">
                        <li><span class="green-bg"></span> Buying Expenses</li>
                        <li><span class="blue-bg"></span> Renting Expenses</li>
                    </ul>
                    <ul class="expenses-block">
                        <li>
                            <h4>Total Buying Expenses</h4>
                            <label :class="[isGreaterThanZero(TotalBuyingExpense) ? 'red-text' : 'green-text']">{{ absoluteWithBrackets(TotalBuyingExpense) }}</label>
                        </li>
                        <li>
                            <h4>Total Renting Expenses</h4>
                            <label :class="[isGreaterThanZero(TotalRentingExpense) ? 'red-text' : 'green-text']">{{ absoluteWithBrackets(TotalRentingExpense) }}</label>
                        </li>
                    </ul>
                </div>
                <div class="cost-breakdown" :class="{ 'visible': ShowBreakdown, '' : !ShowBreakdown}">
                    <h2>Cost Breakdown</h2>
                    <h4>Compare the total cost over time of renting with the total cost of buying.</h4>
                    <div class="table-responsive">
                        <table class="table table-striped table-bordered">
                            <tbody>
                                <tr>
                                    <td width="40%"></td>
                                    <td width="30%" align="right"><h5>Buying</h5></td>
                                    <td width="30%" align="right"><h5>Renting</h5></td>
                                </tr>
                                <tr>
                                    <td>Total Mortgage Paid</td>
                                    <td><b>{{ currencyFormatter(TotalMonthlyMortagePaid) }}</b></td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td class="sub-feilds">
                                        Principal<br />
                                        Interest
                                    </td>
                                    <td class="sub-feilds">
                                        {{ currencyFormatter(TotalPaidTowardsPrincipal) }}<br />
                                        {{ currencyFormatter(TotalInterestExpensePaid) }}
                                    </td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Property Taxes</td>
                                    <td>{{ currencyFormatter(TotalPropertyTaxesPaid) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Purchase Closing Costs</td>
                                    <td>{{ currencyFormatter(TotalClosingCosts) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Home Insurance</td>
                                    <td>{{ currencyFormatter(TotalHomeInsurancePaid) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>HOA Dues</td>
                                    <td>{{ currencyFormatter(TotalOfHOAFees) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Home Maintenance costs</td>
                                    <td>{{ currencyFormatter(TotalHomeMaintenanceCosts) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Selling Expense</td>
                                    <td>{{ currencyFormatter(TotalSellingExpenses) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Total Tax Saving</td>
                                    <td><b>{{ currencyFormatter(TotalTaxSaving) }}</b></td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td class="sub-feilds">
                                        Mortgage interest tax deduction<br />
                                        Unusable mortgage interest deduction<br />
                                        Property tax deduction
                                    </td>
                                    <td class="sub-feilds">
                                        {{ currencyFormatter(TaxSavingMortgageInterestDeduction) }}<br />
                                        {{ currencyFormatter(TaxSavingUnableMortgageInterestDeduction) }}<br />
                                        {{ currencyFormatter(TaxSavingPropertyTaxDeduction) }}
                                    </td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Gain on Home Sale</td>
                                    <td>{{ currencyFormatter(TotalHomeValueAppreciation) }}</td>
                                    <td>-</td>
                                </tr>
                                <tr>
                                    <td>Total Rent Paid</td>
                                    <td>-</td>
                                    <td>{{ currencyFormatter(TotalRentPaid) }}</td>
                                </tr>
                                <tr>
                                    <td>Security Deposit</td>
                                    <td>-</td>
                                    <td>{{ currencyFormatter(TotalSecurityDeposit) }}</td>
                                </tr>
                                <tr>
                                    <td>Renter’s Insurance</td>
                                    <td>-</td>
                                    <td>{{ currencyFormatter(TotalRentersinsurancePaid) }}</td>
                                </tr>
                                <tr>
                                    <td>Investment Income</td>
                                    <td>-</td>
                                    <td>{{ currencyFormatter(InvestmentIncome) }}</td>
                                </tr>
                                <tr>
                                    <td>Total</td>
                                    <td>
                                        <h4 :class="[isGreaterThanZero(TotalBuyingExpense) ? 'red-text' : 'green-text']">
                                            <span v-if="!isBuyingExpensive()" class="lt4 lt4-Check"></span> {{ absoluteWithBrackets(TotalBuyingExpense) }}
                                        </h4></td>
                                    <td>
                                        <h4 :class="[isGreaterThanZero(TotalRentingExpense) ? 'red-text' : 'green-text']">
                                            <span v-if="isBuyingExpensive()" class="lt4 lt4-Check"> </span> {{ absoluteWithBrackets(TotalRentingExpense) }}
                                        </h4>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="text-center">
                    <button class="btn btn-outline" @click="showBreakdownClick()">
                        {{ !ShowBreakdown ? "Show" : "Hide" }} breakdown
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import ZipCodeInput from '~/components/zip-code-input.vue';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LoanTermList from '~/constants/loan-term-list';
    import PaymentHelper from '~/helpers/payment';
    import BaseMixin from '~/mixin/base-mixin';
    import GetLocation from '~/mixin/get-geo-location';
    import StringFormat from '~/helpers/string-format';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import PercentageDollerAmount from '~/mixin/percentage-doller-amount';
    import TokenService from '~/services/token';

    export default {
        props: {
            calcSubTitle: {
                default: 'Should you rent or buy?',
            },
            geoLocation: {
                type: Object,
                default() {
                    return {
                        zipCode: '28277',
                        city: 'Charlotte',
                        state: 'NC',
                    };
                },
            },
            labelPropertyLocation: {
                default: 'Property Location:',
            },
            labelDesiredHomePrice: {
                default: 'Desired Home Price:',
            },
            desiredHomePrice: {
                default: '450000',
            },
            labelDesiredRentPayment: {
                default: 'Desired Rent Payment:',
            },
            desiredRentPayment: {
                default: '2500',
            },
            labelPlannedStay: {
                default: 'Planned Length of Stay:',
            },
            plannedStay: {
                default: '10',
            },
            labelDownPayment: {
                default: 'Down Payment:',
            },
            downPayment: {
                default: '90000',
            },
            downPaymentTooltip: {
                default: DefaultTooltip.DownPayment,
            },
            downPaymentPercentage: {
                default: '20',
            },
            downPaymentTowardsHome: {
                default: '0.2',
            },
            labelInterestRate: {
                default: 'Interest Rate:',
            },
            interestRate: {
                default: '2.25',
            },
            labelMortgageTerm: {
                default: 'Mortgage Term:',
            },
            mortgageTerm: {
                default: 30,
            },
            loanTermInYears: {
                default: 30,
            },
            labelPropertyTax: {
                default: 'Property Tax:',
            },
            propertyTaxValue: {
                default: '4375',
            },
            propertyTax: {
                default: '1.14',
            },
            labelHomeownersInsurance: {
                default: 'Home Insurance:',
            },
            homeownersInsurance: {
                default: '1000',
            },
            labelAnnualHOAFees: {
                default: 'HOA Fees:',
            },
            monthlyHOAFees: {
                default: '50',
            },
            labelAnnualMaintenance: {
                default: 'Annual Maintenance:',
            },
            annualMaintenance: {
                default: '1000',
            },
            labelHomeAppreciation: {
                default: 'Annual Home Appreciation:',
            },
            homeAppreciation: {
                default: '3',
            },
            labelCostSellingHome: {
                default: 'Cost Of Selling Home:',
            },
            costSellingHome: {
                default: '6',
            },
            labelClosingCosts: {
                default: 'Closing Costs:',
            },
            closingCosts: {
                default: '2',
            },
            labelInvestmentReturnRate: {
                default: 'Investment Return Rate:',
            },
            investmentReturnRate: {
                default: '3',
            },
            labelInflation: {
                default: 'Inflation:',
            },
            inflation: {
                default: '2.00',
            },
            labelAnnualIncome: {
                default: 'Annual Income:',
            },
            annualIncome: {
                default: '76500',
            },
            annualIncomeTooltip: {
                default: DefaultTooltip.AnnualIncome,
            },
            labelMarginalIncomeTax: {
                default: 'Marginal Income Tax Rate:',
            },
            marginalIncomeTax: {
                default: '15',
            },
            labelAvgSavingAndLoan: {
                default: 'Avg. Savings & Loan Tax Rate:',
            },
            avgSavingAndLoan: {
                default: '5.00',
            },
            labelOtherTaxDeduction: {
                default: 'Other Tax Deductions:',
            },
            otherTaxDeduction: {
                default: '2500',
            },
            isMarried: {
                default: true,
            },
            labelRentalInflation: {
                default: 'Rental Inflation:',
            },
            rentalInflation: {
                default: '3',
            },
            labelSecurityDeposit: {
                default: 'Security Deposit:',
            },
            securityDeposit: {
                default: '1500',
            },
            labelRentalInsurance: {
                default: 'Annual Rental Insurance:',
            },
            rentalInsurance: {
                default: '180',
            },
            recalculateButtonText: {
                default: 'Recalculate',
            },
            destinationLinkOrId: {
                default: 'ns-hp-purchase',
            },
            destinationButtonText: {
                default: 'View Free Mortgage Offers',
            },
            showAdvancedOptions: {
                default: false,
            },
            showBreakdown: {
                default: false,
            },
            span: {
                type: String,
                default: '',
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '',
            },
            disclosureText: {
                default: 'Advertising Disclosures',
            },
            disclosureStatement: {
                default: 0,
            },
            mortgageCalcUrl: {
                default: '/mortgage-calculator',
            },
            maxValue: {
                default: 999999999,
            },
            interestRateToken: {
                default: '30_FIXED_RATE_350K',
            },
        },
        data() {
            return {
                ZipCode: this.geoLocation.zipCode,
                Location: this.geoLocation,
                ShowZipCodeError: false,
                DesiredHomePrice: this.desiredHomePrice,
                DesiredRentPayment: this.desiredRentPayment,
                PlannedStay: this.plannedStay,
                DownPayment: this.downPayment,
                DownPaymentPercentage: this.downPaymentPercentage,
                DownPaymentTooltip: parseInt(this.hideTooltip, 10) ? '' : this.downPaymentTooltip,
                InterestRateToken: this.interestRateToken,
                InterestRate: this.interestRate,
                FetchedInterestRate: this.interestRate,
                MortgageTerm: this.mortgageTerm,
                MortgageTermYearsList: LoanTermList.LoanTermInYearsList.refinance,
                PropertyTax: this.propertyTax,
                PropertyTaxValue: this.propertyTaxValue,
                HomeownersInsurance: this.homeownersInsurance,
                MonthlyHOAFees: this.monthlyHOAFees,
                AnnualMaintenance: this.annualMaintenance,
                HomeAppreciation: this.homeAppreciation,
                CostSellingHome: this.costSellingHome,
                ClosingCosts: this.closingCosts,
                InvestmentReturnRate: this.investmentReturnRate,
                Inflation: this.inflation,
                AnnualIncome: this.annualIncome,
                AnnualIncomeTooltip: parseInt(this.hideTooltip, 10) ? '' : this.annualIncomeTooltip,
                MarginalIncomeTax: this.marginalIncomeTax,
                AvgSavingAndLoan: this.avgSavingAndLoan,
                OtherTaxDeduction: this.otherTaxDeduction,
                IsMarried: this.isMarried,
                RentalInflation: this.rentalInflation,
                SecurityDeposit: this.securityDeposit,
                RentalInsurance: this.rentalInsurance,
                TotalHomeMaintenanceCosts: 0,
                TaxSavingMortgageInterestDeduction: 0,
                TaxSavingUnableMortgageInterestDeduction: 0,
                TaxSavingPropertyTaxDeduction: 0,
                ShowAdvancedOptions: this.showAdvancedOptions,
                ShowBreakdown: this.showBreakdown,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                ChartWidth: 750,
                ChartHeight: 250,
                TotalMonthlyMortagePaid: 0,
                TotalPropertyTaxesPaid: 0,
                TotalClosingCosts: 0,
                TotalHomeInsurancePaid: 0,
                TotalOfHOAFees: 0,
                TotalSellingExpenses: 0,
                TotalTaxSaving: 0,
                HomeValueAppreciation: 0,
                TotalInterestExpensePaid: 0,
                TotalPaidTowardsPrincipal: 0,
                TotalBuyingExpense: 0,
                TotalRentPaid: 0,
                InvestmentIncome: 0,
                TotalSecurityDeposit: 0,
                TotalRentersinsurancePaid: 0,
                TotalRentingExpense: 0,
                TotalHomeMaintenance: 0,
                TotalHomeValueAppreciation: 0,
                GraphResult: '',
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            label: 'Renting Expenses',
                            fill: false,
                            data: [],
                            borderColor: '#00aeef',
                            pointRadius: 0,
                        },
                        {
                            label: 'Buying Expenses',
                            fill: false,
                            data: [],
                            borderColor: '#09c176',
                            pointRadius: 0,
                        },
                    ],
                },
                options: {
                    legend: {
                        display: false,
                    },
                    scales: {
                        yAxes: [{
                            scaleLabel: {
                                display: true,
                                labelString: 'Expenses',
                            },
                            ticks: {
                                min: 0,
                                max: 2000000,
                                callback(value) {
                                    return value > 0 ? `${value / 1000}K` : 0;
                                },
                            },
                            gridLines: {
                                color: '#eee',
                            },
                        }],
                        xAxes: [{
                            scaleLabel: {
                                display: true,
                                labelString: 'Year',
                            },
                            ticks: {
                                min: 1,
                                autoSkip: false,
                                callback(value) {
                                    return value % 1 === 0 ? value : null;
                                },
                            },
                            gridLines: {
                                display: false,
                            },
                        }],
                    },
                    responsive: true,
                    maintainAspectRatio: false,
                    tooltips: {
                        intersect: false,
                        enabled: false,
                        custom(tooltip) {
                            let tooltipEl = document.getElementById('chartjs-tooltip');
                            if (!tooltipEl) {
                                tooltipEl = document.createElement('div');
                                tooltipEl.id = 'chartjs-tooltip';
                                tooltipEl.innerHTML = '<div></div>';
                                this._chart.canvas.parentNode.appendChild(tooltipEl);
                            }
                            if (Number(tooltip.opacity) === 0) {
                                tooltipEl.style.opacity = 0;
                                return;
                            }
                            tooltip.displayColors = false;
                            tooltipEl.classList.remove('above', 'below', 'no-transform');
                            if (tooltip.yAlign) {
                                tooltipEl.classList.add(tooltip.yAlign);
                            } else {
                                tooltipEl.classList.add('no-transform');
                            }

                            function getBody(bodyItem) {
                                return bodyItem.lines;
                            }
                            if (tooltip.body) {
                                let innerHtml = '';
                                const bodyLines = tooltip.body.map(getBody);
                                bodyLines.forEach((body) => {
                                    const bodaySplit = body[0].split(':');
                                    if (tooltip.dataPoints[0].xLabel != null) {
                                        innerHtml = `<label>${Math.round(tooltip.dataPoints[0].xLabel)} Year</label>`;
                                    }
                                    innerHtml += `<label>${bodaySplit[0]}</label>`;
                                    innerHtml += `<strong>${Accounting.formatMoney(bodaySplit[1], { precision: 0 })}</strong>`;
                                });
                                const tableRoot = tooltipEl.querySelector('div');
                                tableRoot.innerHTML = innerHtml;
                            }
                            const positionY = this._chart.canvas.offsetTop;
                            const positionX = this._chart.canvas.offsetLeft;
                            tooltipEl.style.opacity = 1;
                            tooltipEl.style.left = `${positionX - 25 + tooltip.caretX}px`;
                            tooltipEl.style.top = `${positionY - 75 + tooltip.caretY}px`;
                        },
                    },
                },
            };
        },
        computed: {
            generatePostData() {
                // Direct extraction of needed values with proper unformatting
                return {
                    AnnualIncome: Accounting.unformat(this.AnnualIncome),
                    AnnualMaintenance: Accounting.unformat(this.AnnualMaintenance),
                    AvgSavingAndLoan: Accounting.unformat(this.AvgSavingAndLoan),
                    ClosingCosts: Accounting.unformat(this.ClosingCosts),
                    CostSellingHome: Accounting.unformat(this.CostSellingHome),
                    DesiredHomePrice: Accounting.unformat(this.DesiredHomePrice),
                    DesiredRentPayment: Accounting.unformat(this.DesiredRentPayment),
                    DownPayment: Accounting.unformat(this.DownPayment),
                    DownPaymentPercentage: Accounting.unformat(this.DownPaymentPercentage),
                    HomeAppreciation: Accounting.unformat(this.HomeAppreciation),
                    HomeownersInsurance: Accounting.unformat(this.HomeownersInsurance),
                    Inflation: Accounting.unformat(this.Inflation),
                    InterestRate: Accounting.unformat(this.InterestRate),
                    InvestmentReturnRate: Accounting.unformat(this.InvestmentReturnRate),
                    IsMarried: this.IsMarried,
                    MarginalIncomeTax: Accounting.unformat(this.MarginalIncomeTax),
                    MonthlyHOAFees: Accounting.unformat(this.MonthlyHOAFees),
                    MortgageTerm: Accounting.unformat(this.MortgageTerm),
                    OtherTaxDeduction: Accounting.unformat(this.OtherTaxDeduction),
                    PlannedStay: Accounting.unformat(this.PlannedStay),
                    PropertyTax: Accounting.unformat(this.PropertyTax),
                    RentalInflation: Accounting.unformat(this.RentalInflation),
                    RentalInsurance: Accounting.unformat(this.RentalInsurance),
                    SecurityDeposit: Accounting.unformat(this.SecurityDeposit),
                };
            },
        },
        methods: {
            calculateAndViewMore() {
                this.calculate();
                this.viewMoreClick();
            },
            viewMoreClick() {
                this.ShowAdvancedOptions = !this.ShowAdvancedOptions;
            },
            showBreakdownClick() {
                this.ShowBreakdown = !this.ShowBreakdown;
            },
            resetFilter() {
                this.ZipCode = this.geoLocation.zipCode;
                this.getLocationByZip(this.ZipCode);
                this.DesiredHomePrice = Accounting.formatNumber(this.desiredHomePrice);
                this.DesiredRentPayment = Accounting.formatNumber(this.desiredRentPayment);
                this.PlannedStay = this.plannedStay;
                this.DownPayment = Accounting.formatNumber(this.downPayment);
                this.InterestRate = Accounting.formatNumber(this.FetchedInterestRate, { precision: 3 });
                this.MortgageTerm = this.mortgageTerm;
                this.PropertyTax = Accounting.formatNumber(this.propertyTax, { precision: 2 });
                this.HomeownersInsurance = Accounting.formatNumber(this.homeownersInsurance);
                this.MonthlyHOAFees = Accounting.formatNumber(this.monthlyHOAFees);
                this.AnnualMaintenance = Accounting.formatNumber(this.annualMaintenance);
                this.HomeAppreciation = Accounting.formatNumber(this.homeAppreciation, { precision: 2 });
                this.CostSellingHome = Accounting.formatNumber(this.costSellingHome, { precision: 2 });
                this.ClosingCosts = Accounting.formatNumber(this.closingCosts, { precision: 2 });
                this.InvestmentReturnRate = Accounting.formatNumber(this.investmentReturnRate, { precision: 2 });
                this.Inflation = Accounting.formatNumber(this.inflation, { precision: 2 });
                this.AnnualIncome = Accounting.formatNumber(this.annualIncome);
                this.MarginalIncomeTax = Accounting.formatNumber(this.marginalIncomeTax, { precision: 2 });
                this.AvgSavingAndLoan = Accounting.formatNumber(this.avgSavingAndLoan, { precision: 2 });
                this.OtherTaxDeduction = Accounting.formatNumber(this.otherTaxDeduction);
                this.IsMarried = this.isMarried;
                this.RentalInflation = Accounting.formatNumber(this.rentalInflation, { precision: 2 });
                this.SecurityDeposit = Accounting.formatNumber(this.securityDeposit);
                this.RentalInsurance = Accounting.formatNumber(this.rentalInsurance);
                this.downPaymentChange(this.downPayment);
                this.PropertyTaxValue = this.getDollarAmount(this.propertyTax, this.desiredHomePrice);
                this.calculate();
            },
            getLocationByZip(zip) {
                this.ZipCode = zip;
                if (this.isZipCodeValidFormat(zip)) {
                    GetLocation.methods.GetZipCode(zip, this.setLocationData);
                } else {
                    this.ShowZipCodeError = true;
                }
            },
            isZipCodeValidFormat(zip) {
                const zipCodeRegEx = /^\d{5}$/;
                return zipCodeRegEx.test(zip) && zip !== '';
            },
            setLocationData(geoData) {
                const isError = 'err' in geoData && geoData.err === true;
                if (isError) {
                    this.ShowZipCodeError = true;
                } else {
                    this.ShowZipCodeError = false;
                    const cityNameFormatted = StringFormat.capitalize(geoData.city);
                    geoData.city = cityNameFormatted;
                    this.Location = geoData;
                }
            },
            calculateIntialMortgagePaidValue(i, j, mortgageMonthlyPayment, mortgagePaidInterest, mortgagePaidPrincipal, mortgagePaidBalance) {
                const data = {
                    DesiredHomePrice: Accounting.unformat(this.DesiredHomePrice),
                    DownPayment: Accounting.unformat(this.DownPayment),
                    InterestRate: Accounting.unformat(this.InterestRate),
                };
                const interest = (data.DesiredHomePrice - data.DownPayment) * (data.InterestRate / 100.00 / 12);
                mortgagePaidInterest[i][j] = interest;
                mortgagePaidPrincipal[i][j] = mortgageMonthlyPayment - interest;
                mortgagePaidBalance[i][j] = data.DesiredHomePrice - data.DownPayment - (mortgageMonthlyPayment - interest);
                return {
                    sumMortgagePaidInterest: mortgagePaidInterest[i][j],
                    sumMortgagePaidPrincipal: mortgagePaidPrincipal[i][j],
                    sumMortgagePaidBalance: mortgagePaidBalance[i][j],
                };
            },
            calculateMortgagePaidValue(i, j, mortgageMonthlyPayment, mortgagePaidInterest, mortgagePaidPrincipal, mortgagePaidBalance, sumMortgagePaidInterest, sumMortgagePaidPrincipal, sumMortgagePaidBalance) {
                let k = i;
                let l = j;
                if (i > 0) {
                    if (l === 0) {
                        k--;
                        l = 12;
                    }
                }
                const interest = Accounting.unformat(mortgagePaidBalance[k][l - 1] * (Accounting.unformat(this.InterestRate) / 100.00 / 12));
                mortgagePaidInterest[i][j] = interest;
                mortgagePaidPrincipal[i][j] = mortgageMonthlyPayment - interest;
                mortgagePaidBalance[i][j] = mortgagePaidBalance[k][l - 1] - (mortgageMonthlyPayment - interest);
                return {
                    sumMortgagePaidInterest: sumMortgagePaidInterest[sumMortgagePaidInterest.length - 1] + mortgagePaidInterest[i][j],
                    sumMortgagePaidPrincipal: sumMortgagePaidPrincipal[sumMortgagePaidPrincipal.length - 1] + mortgagePaidPrincipal[i][j],
                    sumMortgagePaidBalance: sumMortgagePaidBalance[sumMortgagePaidBalance.length - 1] + mortgagePaidBalance[i][j],
                };
            },
            CUMIPMT(loanAmount, end, mortgagePayment, rate) {
                const payment = mortgagePayment;
                let interest = 0.0;
                let totalinterest = 0.0;
                let balance = loanAmount;
                let principal = 0.0;
                for (let i = 1; i <= end; i++) {
                    interest = balance * rate;
                    totalinterest += interest;
                    principal = payment - interest;
                    balance -= principal;
                }
                return totalinterest;
            },
            convertToNumber(figure) {
                return Math.round(this.unformatFigure(figure));
            },
            calculate() {
                // Helper function to sum arrays with proper number conversion
                // without using eval ¯\_(ツ)_/¯
                const sumArray = arr => arr.reduce((sum, val) => sum + Number(val), 0);

                const response = this.generatePostData;
                this.chartData.labels = [];
                this.chartData.datasets[1].data = [];
                this.chartData.datasets[0].data = [];
                const monthlyHomeInsuranceValues = [];
                const totalMonthlyExpensesValues = [];
                const totalRentersinsurancePaidValues = [];
                const propertyTaxesPaidValues = [];
                const desiredHomePriceValues = [];
                const investmentIncomeArray = [];
                const totalMortgagePaidPrincipalValues = [];
                const totalMortgagePaidInterestValues = [];
                const rentPaidForYear = response.DesiredRentPayment * 12;

                monthlyHomeInsuranceValues.push(response.HomeownersInsurance);
                totalMonthlyExpensesValues.push(response.MonthlyHOAFees);
                totalRentersinsurancePaidValues.push(response.RentalInsurance);
                desiredHomePriceValues.push(response.DesiredHomePrice);
                investmentIncomeArray.push(0);
                totalMortgagePaidPrincipalValues.push(0);
                totalMortgagePaidInterestValues.push(0);
                for (let i = 0; i <= response.PlannedStay; i++) {
                    if (i < response.PlannedStay - 1) {
                        monthlyHomeInsuranceValues.push(monthlyHomeInsuranceValues[i] * (1 + response.Inflation / 100));
                        totalMonthlyExpensesValues.push(totalMonthlyExpensesValues[i] * (1 + response.Inflation / 100));
                        totalRentersinsurancePaidValues.push(totalRentersinsurancePaidValues[i] * (1 + response.Inflation / 100));
                    }
                    if (i < response.PlannedStay) {
                        desiredHomePriceValues.push(Accounting.unformat(desiredHomePriceValues[i]) + Accounting.unformat(desiredHomePriceValues[i]) * response.HomeAppreciation / 100);
                        propertyTaxesPaidValues.push(Accounting.unformat(desiredHomePriceValues[i]) * (response.PropertyTax / 100));
                    }
                    // Investment income changes
                    if (i >= 1) {
                        investmentIncomeArray.push(i === 1
                            ? response.DownPayment * response.InvestmentReturnRate / 100.00
                            : investmentIncomeArray[i - 1] * (1 + response.InvestmentReturnRate / 100.00));
                    }
                }

                // Sum of investmentIncomeArray into investmentIncome
                const investmentIncome = -1 * this.getTotalYearValue(investmentIncomeArray);
                const loanAmount = response.DesiredHomePrice - response.DownPayment;
                const loanTermInMonths = response.MortgageTerm * 12;
                const monthlyRate = isNaN(response.InterestRate / 1200) ? 0 : response.InterestRate / 1200;
                const numberOfPayments = response.PlannedStay * 12;
                const appreciateHomeValue = response.DesiredHomePrice * (1 + response.HomeAppreciation / 100) ** response.PlannedStay;
                const mortgagePayment = PaymentHelper.getPayment(loanAmount, loanTermInMonths, monthlyRate);
                const monthlyMaintenance = isNaN(response.AnnualMaintenance / 12) ? 0 : response.AnnualMaintenance;
                const totalHomeMaintenance = monthlyMaintenance * numberOfPayments;
                const totalOfMonthlyMortagePaid = mortgagePayment * numberOfPayments + response.DownPayment;
                const totalOfInterestExpensePaid = this.CUMIPMT(loanAmount, numberOfPayments, mortgagePayment, monthlyRate);
                const totalClosingCosts = response.ClosingCosts / 100 * response.DesiredHomePrice;
                const totalPropertyTaxesPaid = sumArray(propertyTaxesPaidValues);
                const totalHomeInsurancePaid = sumArray(monthlyHomeInsuranceValues);
                const totalMonthlyExpenses = sumArray(totalMonthlyExpensesValues);
                const totalSellingExpenses = appreciateHomeValue * (response.CostSellingHome / 100);
                let totalRentersinsurancePaid = sumArray(totalRentersinsurancePaidValues);
                let totalRentPaid = response.RentalInflation > 0 ? rentPaidForYear * ((1 + response.RentalInflation / 100) ** response.PlannedStay - 1) / (response.RentalInflation / 100) : rentPaidForYear * response.PlannedStay;
                const totalOfRentingExpense = totalRentPaid + totalRentersinsurancePaid + response.SecurityDeposit + investmentIncome;
                let totalRentingExpense = isNaN(totalOfRentingExpense) ? 0 : totalOfRentingExpense;
                let totalInterestExpensePaid = isNaN(totalOfInterestExpensePaid) ? 0 : totalOfInterestExpensePaid;
                let homeValueAppreciation = -(appreciateHomeValue - response.DesiredHomePrice);

                let totalTaxSaving = -(totalInterestExpensePaid + totalPropertyTaxesPaid) * (response.MarginalIncomeTax / 100);
                // START: Tax Saving and Home Maintenance Cost Calculations
                const mortgageBalance = [];
                const homeValue = [];
                const propertyTaxesPaid = [];
                const totalDeduction = [];
                const mortgagePaidInterest = [];
                const mortgagePaidPrincipal = [];
                const mortgagePaidBalance = [];
                const sumMortgagePaidInterest = [];
                const sumMortgagePaidPrincipal = [];
                const sumMortgagePaidBalance = [];
                const taxSavingMortgageInterestTaxDeduction = [];
                const taxSavingMortgageInterestTaxDeductionWithoutRound = [];
                const taxSavingUnusableMortgageInterestDeduction = [];
                const taxSavingsPropertyTaxDeduction = [];
                const interestRatePerMonth = response.InterestRate / 100.00 / 12.00;
                const savingAndLoanTax = response.AnnualIncome * (response.AvgSavingAndLoan / 100);
                const desiredHomePriceAndDownPaymentDifference = response.DesiredHomePrice - response.DownPayment;
                // Vales are actual for 2025
                const standardDeduction = this.IsMarried ? 30000 : 15000;
                let homeMaintenanceCostResult = 0;
                let taxSavingMortgageInterestDeductionResult = 0;
                let taxSavingUnusedMortgageInterestDeductionResult = 0;
                let taxSavingPropertyTaxDeductionResult = 0;
                let AmortizationCounter = 0;

                mortgageBalance.push(response.DesiredHomePrice * (1 - response.DownPaymentPercentage / 100));
                homeValue.push(response.DesiredHomePrice);
                propertyTaxesPaid.push(0);
                totalDeduction.push(0);
                taxSavingsPropertyTaxDeduction.push(0);
                const mortgageMonthlyPayment = Accounting.unformat(PaymentHelper.compute_PMT(desiredHomePriceAndDownPaymentDifference, interestRatePerMonth, loanTermInMonths));
                for (let i = 1; i <= response.PlannedStay; i++) { // years
                    // Calculating Total Mortgage Paid-Principle and Interest using Monthly Principle, Interest and Balance
                    mortgagePaidInterest.push([]);
                    mortgagePaidPrincipal.push([]);
                    mortgagePaidBalance.push([]);
                    for (let j = 0; j < 12; j++) { // months
                        AmortizationCounter++;
                        if (i === 1 && j === 0) {
                            const value = this.calculateIntialMortgagePaidValue(i - 1, j, mortgageMonthlyPayment, mortgagePaidInterest, mortgagePaidPrincipal, mortgagePaidBalance);
                            sumMortgagePaidInterest.push(value.sumMortgagePaidInterest);
                            sumMortgagePaidPrincipal.push(value.sumMortgagePaidPrincipal);
                            sumMortgagePaidBalance.push(value.sumMortgagePaidBalance);
                        } else if (AmortizationCounter <= response.MortgageTerm * 12) {
                            const value = this.calculateMortgagePaidValue(i - 1, j, mortgageMonthlyPayment, mortgagePaidInterest, mortgagePaidPrincipal, mortgagePaidBalance, sumMortgagePaidInterest, sumMortgagePaidPrincipal, sumMortgagePaidBalance);
                            sumMortgagePaidInterest.push(value.sumMortgagePaidInterest);
                            sumMortgagePaidPrincipal.push(value.sumMortgagePaidPrincipal);
                            sumMortgagePaidBalance.push(value.sumMortgagePaidBalance);
                        }
                    }
                    const totalMortgagePaidInterest = this.getTotalYearValue(mortgagePaidInterest[i - 1]) * -1;
                    const totalMortgagePaidPrincipal = this.getTotalYearValue(mortgagePaidPrincipal[i - 1]) * -1;
                    totalMortgagePaidPrincipalValues.push(totalMortgagePaidPrincipal);
                    totalMortgagePaidInterestValues.push(totalMortgagePaidInterest);
                    // Home Maintenance Cost
                    homeMaintenanceCostResult += (1 + response.Inflation / 100) ** (i - 1);
                    // Calculation of Tax Saving Fields
                    mortgageBalance[i] = mortgageBalance[i - 1] + totalMortgagePaidPrincipal;
                    homeValue[i] = homeValue[i - 1] * (1 + response.HomeAppreciation / 100);
                    propertyTaxesPaid[i] = Accounting.unformat(homeValue[i - 1] * (response.PropertyTax / 100) * -1);
                    if (i <= response.PlannedStay) {
                        // Tax Savings-Mortgage Interest Tax Deduction
                        if (mortgageBalance[i - 1] > 750000) {
                            taxSavingMortgageInterestDeductionResult += response.MarginalIncomeTax / 100.00 * 750000.00 * (response.InterestRate / 100.00);
                            taxSavingMortgageInterestTaxDeductionWithoutRound.push(response.MarginalIncomeTax / 100.00 * totalMortgagePaidInterest * -1);
                            taxSavingMortgageInterestTaxDeduction.push(response.MarginalIncomeTax / 100.00 * totalMortgagePaidInterest * -1);
                        } else {
                            taxSavingMortgageInterestTaxDeduction.push(response.MarginalIncomeTax / 100.00 * totalMortgagePaidInterest * -1);
                            taxSavingMortgageInterestDeductionResult = this.isNullOrEmpty(taxSavingMortgageInterestDeductionResult) + this.isNullOrEmpty(taxSavingMortgageInterestTaxDeduction[i - 1]);
                        }
                        // Tax Savings-(Unusable mortgage interest deduction)
                        totalDeduction[i] = Math.min(savingAndLoanTax - propertyTaxesPaid[i], 10000) + parseInt(response.OtherTaxDeduction, 10) + this.isNullOrEmpty(taxSavingMortgageInterestTaxDeduction[i - 1]) / (response.MarginalIncomeTax / 100.00);
                        taxSavingUnusedMortgageInterestDeductionResult = this.isNullOrEmpty(taxSavingUnusedMortgageInterestDeductionResult);
                        if (i < response.PlannedStay) {
                            if (totalDeduction[i] < standardDeduction) {
                                taxSavingUnusableMortgageInterestDeduction.push(-1 * this.isNullOrEmpty(taxSavingMortgageInterestTaxDeduction[i - 1]));
                                taxSavingUnusedMortgageInterestDeductionResult += taxSavingUnusableMortgageInterestDeduction[i - 1];
                            } else {
                                const unusableSaving = -1 * (response.MarginalIncomeTax / 100 * Math.max(0, standardDeduction - (totalDeduction[i] - this.isNullOrEmpty(taxSavingMortgageInterestTaxDeduction[i - 1]) / (response.MarginalIncomeTax / 100.00))));
                                taxSavingUnusableMortgageInterestDeduction.push(this.isNullOrEmpty(unusableSaving));
                                taxSavingUnusedMortgageInterestDeductionResult += this.isNullOrEmpty(taxSavingUnusableMortgageInterestDeduction[i - 1]);
                            }
                        } else if (i === Number(response.PlannedStay)) {
                            taxSavingUnusableMortgageInterestDeduction.push(0);
                        }
                        // Tax Savings-Property Tax Deduction
                        if (totalDeduction[i] <= standardDeduction) {
                            taxSavingsPropertyTaxDeduction.push(0);
                            taxSavingPropertyTaxDeductionResult += taxSavingsPropertyTaxDeduction[i];
                        } else {
                            taxSavingsPropertyTaxDeduction.push(Math.min(Math.max(0, 10000 - savingAndLoanTax), -1 * propertyTaxesPaid[i]) * (response.MarginalIncomeTax / 100));
                            taxSavingPropertyTaxDeductionResult += taxSavingsPropertyTaxDeduction[i];
                        }
                    } else {
                        taxSavingMortgageInterestTaxDeduction.push(0);
                        taxSavingMortgageInterestTaxDeductionWithoutRound.push(0);
                        taxSavingUnusableMortgageInterestDeduction.push(0);
                        taxSavingsPropertyTaxDeduction.push(0);
                    }
                    // Total Of Savings
                    totalTaxSaving = this.convertToNumber(taxSavingMortgageInterestDeductionResult) + this.convertToNumber(taxSavingUnusedMortgageInterestDeductionResult) + this.convertToNumber(taxSavingPropertyTaxDeductionResult);
                    // Gain On Home Sale
                    homeValueAppreciation = i === Number(response.PlannedStay) ? homeValue[i] - mortgageBalance[i] : 0;
                }
                const totalHomeMaintenanceCosts = response.AnnualMaintenance * homeMaintenanceCostResult;
                const taxSavingMortgageInterestDeduction = -1 * Accounting.unformat(taxSavingMortgageInterestDeductionResult);
                const taxSavingUnableMortgageInterestDeduction = -1 * Accounting.unformat(taxSavingUnusedMortgageInterestDeductionResult);
                const taxSavingPropertyTaxDeduction = -1 * Accounting.unformat(taxSavingPropertyTaxDeductionResult);
                const totalHomeValueAppreciation = -1 * Accounting.unformat(homeValueAppreciation);

                const sumOfMortgagePaidPrincipal = sumArray(totalMortgagePaidPrincipalValues);
                const sumOfMortgagePaidInterest = sumArray(totalMortgagePaidInterestValues);
                this.TotalPaidTowardsPrincipal = (parseFloat(sumOfMortgagePaidPrincipal) - parseFloat(response.DownPayment)) * -1;
                this.TotalInterestExpensePaid = parseFloat(sumOfMortgagePaidInterest) * -1;
                // Total Buying Expense
                const totalOfBuyingExpense = this.TotalPaidTowardsPrincipal + this.TotalInterestExpensePaid + totalPropertyTaxesPaid + totalClosingCosts + totalHomeInsurancePaid
                    + totalMonthlyExpenses + totalHomeMaintenanceCosts + totalSellingExpenses + taxSavingMortgageInterestDeduction
                    + taxSavingUnableMortgageInterestDeduction + taxSavingPropertyTaxDeduction + totalHomeValueAppreciation;
                const totalBuyingExpense = isNaN(totalOfBuyingExpense) ? 0 : totalOfBuyingExpense;

                this.TotalHomeMaintenanceCosts = totalHomeMaintenanceCosts;
                this.TaxSavingMortgageInterestDeduction = taxSavingMortgageInterestDeduction;
                this.TaxSavingUnableMortgageInterestDeduction = taxSavingUnableMortgageInterestDeduction;
                this.TaxSavingPropertyTaxDeduction = taxSavingPropertyTaxDeduction;
                this.TotalHomeValueAppreciation = totalHomeValueAppreciation;
                // END: Tax Saving and Home Maintenance Cost Calculations
                this.TotalMonthlyMortagePaid = totalOfMonthlyMortagePaid;
                this.TotalClosingCosts = totalClosingCosts;
                this.TotalPropertyTaxesPaid = totalPropertyTaxesPaid;
                this.TotalHomeInsurancePaid = totalHomeInsurancePaid;
                this.TotalSellingExpenses = totalSellingExpenses;
                this.TotalTaxSaving = totalTaxSaving;
                this.HomeValueAppreciation = homeValueAppreciation;
                this.TotalBuyingExpense = totalBuyingExpense;
                this.TotalHomeMaintenance = totalHomeMaintenance;
                this.TotalOfHOAFees = totalMonthlyExpenses;
                this.TotalRentPaid = totalRentPaid;
                this.TotalSecurityDeposit = response.SecurityDeposit;
                this.TotalRentersinsurancePaid = totalRentersinsurancePaid;
                this.InvestmentIncome = investmentIncome;
                this.TotalRentingExpense = totalRentingExpense;

                let g = 0;
                let yVal = 0;
                let yValue = 0;
                for (let i = 1; i <= loanTermInMonths; i++) {
                    const mortgagePaymentForTotalInterest = loanAmount * monthlyRate / (1 - 1 / (1 + monthlyRate) ** i);
                    totalInterestExpensePaid = this.CUMIPMT(loanAmount, numberOfPayments, mortgagePaymentForTotalInterest, monthlyRate);
                    totalTaxSaving = taxSavingMortgageInterestDeductionResult + taxSavingUnusedMortgageInterestDeductionResult + taxSavingPropertyTaxDeductionResult;
                    const totalBuyingCost = totalInterestExpensePaid + totalClosingCosts + totalPropertyTaxesPaid + totalHomeInsurancePaid + totalHomeMaintenanceCosts
                        + totalMonthlyExpenses + totalSellingExpenses + totalTaxSaving + homeValueAppreciation;
                    yVal = totalBuyingCost > 0 ? totalBuyingCost : totalBuyingCost * -1;
                    if (response.RentalInflation > 0) totalRentPaid = rentPaidForYear * ((1 + response.RentalInflation / 100) ** (i / 12) - 1) / (response.RentalInflation / 100);
                    else totalRentPaid = rentPaidForYear * (i / 12);

                    totalRentersinsurancePaid = response.RentalInsurance * response.PlannedStay;
                    totalRentingExpense = this.convertToNumber(totalRentPaid) + this.convertToNumber(totalRentersinsurancePaid) + this.convertToNumber(response.SecurityDeposit) + this.convertToNumber(investmentIncome);
                    yValue = totalRentingExpense;
                    this.chartData.labels.push(i / 12);
                    yValue = yValue >= 0 ? i / 12 >= 1 ? yValue : null : null;
                    yVal = yVal >= 0 ? i / 12 >= 1 ? yVal : null : null;
                    this.chartData.datasets[1].data.push(yVal);
                    this.chartData.datasets[0].data.push(yValue);
                    if (yValue > yVal) {
                        if (g === 0) {
                            g = i;
                        }
                    }
                }
                if (g > 0) {
                    let years = parseInt(g / 12, 10);
                    let months = g % 12;
                    if (months === 11) {
                        years += 1;
                        months = 0;
                    }
                    months += 1;
                    this.GraphResult = 'Buying is more beneficial if you plan on living in this <span>area for more than <strong>';
                    if (years === 1) {
                        this.GraphResult += `${years} year ${months} months</strong>.</span>`;
                    } else if (years > 1 && months > 0) {
                        this.GraphResult += `${years} years ${months} months</strong>.</span>`;
                    } else if (years === 0) {
                        this.GraphResult += `${months} months</strong>.</span>`;
                    }
                } else if (g === 0) {
                    this.GraphResult = 'Buying is not more beneficial than renting';
                }
            },
            isBuyingExpensive() {
                return Accounting.unformat(this.TotalBuyingExpense) >= Accounting.unformat(this.TotalRentingExpense);
            },
            isGreaterThanZero(val) {
                return Accounting.unformat(val) >= 0;
            },
            absoluteWithBrackets(figure) {
                const value = Accounting.formatMoney(Math.abs(figure), { precision: 0 });
                return this.isNegative(figure) ? `(${value})` : value;
            },
            isNegative(figure) {
                return figure < 0;
            },
            restrictPlannedStay() {
                if (Number(this.PlannedStay) === 0 || isNaN(Number(this.PlannedStay)) || this.PlannedStay === '') {
                    this.PlannedStay = 1;
                }
            },
            restrictPercentage(val) {
                return val > 100 ? 100 : val;
            },
            getTotalYearValue(dataList) {
                let value = 0;
                if (dataList) {
                    const keys = Object.keys(dataList);
                    keys.forEach((key) => {
                        if (dataList[key]) {
                            value += dataList[key];
                        }
                    });
                }
                return value;
            },
            propertyTaxValueChange(value) {
                let propertyTaxValue = this.unformat(value);
                const homePrice = this.unformat(this.DesiredHomePrice);
                if (propertyTaxValue > homePrice) {
                    propertyTaxValue = homePrice;
                    this.PropertyTaxValue = Accounting.formatNumber(propertyTaxValue);
                } else if (propertyTaxValue > this.maxValue) {
                    this.PropertyTaxValue = Accounting.formatNumber(this.maxValue);
                    propertyTaxValue = this.maxValue;
                }
                this.PropertyTax = this.getPercentageAmount(propertyTaxValue, homePrice);
                this.calculate();
            },
            propertyTaxChange(value) {
                let propertyTaxValue = this.unformat(value);
                const homePrice = this.unformat(this.DesiredHomePrice);
                if (propertyTaxValue > homePrice) {
                    propertyTaxValue = homePrice;
                    this.PropertyTax = Accounting.formatNumber(propertyTaxValue);
                }
                this.PropertyTaxValue = this.getDollarAmount(propertyTaxValue, homePrice);
                this.calculate();
            },
            downPaymentChange(value) {
                let downPaymentValue = this.unformat(value);
                const homePrice = this.unformat(this.DesiredHomePrice);
                if (downPaymentValue > homePrice) {
                    downPaymentValue = homePrice;
                    this.DownPayment = Accounting.formatNumber(downPaymentValue);
                } else if (downPaymentValue > this.maxValue) {
                    this.DownPayment = Accounting.formatNumber(this.maxValue);
                    downPaymentValue = this.maxValue;
                }
                this.DownPaymentPercentage = this.getPercentageAmount(downPaymentValue, homePrice);
                this.calculate();
            },
            downPaymentPercentChange(value) {
                this.DownPayment = Accounting.formatNumber(this.getDollarAmount(value, this.DesiredHomePrice));
                if (this.unformat(this.DownPayment) > this.unformat(this.DesiredHomePrice)) {
                    this.DownPayment = Accounting.formatNumber(this.DesiredHomePrice);
                }
                this.calculate();
            },
            isNullOrEmpty(number) {
                return isNaN(number) || number === 'Infinity' || number === undefined ? 0 : number;
            },
            updateInitialInterestRate(rate) {
                this.FetchedInterestRate = rate || this.interestRate;
                this.InterestRate = this.FetchedInterestRate;
                this.calculate();
            },
        },
        mounted() {
            this.DownPayment = Accounting.formatNumber(this.getDollarAmount(this.downPaymentPercentage, this.desiredHomePrice));
            this.PropertyTaxValue = this.getDollarAmount(this.propertyTax, this.desiredHomePrice);
            TokenService.GetTokenValue(this.InterestRateToken, this.updateInitialInterestRate);
            this.calculate();
        },
        watch: {
            generatePostData: {
                handler() {
                    this.calculate();
                },
                deep: true,
            },
            DesiredHomePrice() {
                this.DownPayment = Accounting.formatNumber(this.downPaymentTowardsHome * Accounting.unformat(this.DesiredHomePrice));
                this.PropertyTaxValue = this.getDollarAmount(this.PropertyTax, this.DesiredHomePrice);
            },
            PlannedStay() {
                if (this.PlannedStay > 50) {
                    this.PlannedStay = 50;
                }
            },
            InterestRate() {
                this.InterestRate = this.restrictPercentage(this.InterestRate);
            },
            PropertyTax() {
                this.PropertyTax = this.restrictPercentage(this.PropertyTax);
            },
            HomeAppreciation() {
                this.HomeAppreciation = this.restrictPercentage(this.HomeAppreciation);
            },
            MarginalIncomeTax() {
                this.MarginalIncomeTax = this.restrictPercentage(this.MarginalIncomeTax);
            },
            ClosingCosts() {
                this.ClosingCosts = this.restrictPercentage(this.ClosingCosts);
            },
            InvestmentReturnRate() {
                this.InvestmentReturnRate = this.restrictPercentage(this.InvestmentReturnRate);
            },
            RentalInflation() {
                this.RentalInflation = this.restrictPercentage(this.RentalInflation);
            },
            CostSellingHome() {
                this.CostSellingHome = this.restrictPercentage(this.CostSellingHome);
            },
            DownPaymentPercentage() {
                this.DownPaymentPercentage = this.restrictPercentage(this.DownPaymentPercentage);
            },
        },
        components: {
            NumberInput,
            DropDown,
            ZipCodeInput,
            VueChart,
            LtLogo,
            AdvertisingDisclosures,
        },
        mixins: [GetLocation, BaseMixin, PercentageDollerAmount],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
